import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API, detailFromError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const VARIANT = {
  pending: "secondary",
  scraping: "default",
  downloading_pdfs: "default",
  packaging: "default",
  completed: "default",
  failed: "destructive",
  cookie_expired: "destructive",
};

function formatBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function RunHistory() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/runs?limit=50")
      .then((res) => { if (!cancelled) setRuns(res.data.runs || []); })
      .catch((e) => toast.error(detailFromError(e)))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight" data-testid="history-page-title">Run History</h1>
      <Card>
        <CardHeader>
          <CardTitle>Past runs</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-muted-foreground">Loading…</div>
          ) : runs.length === 0 ? (
            <div className="text-muted-foreground" data-testid="history-empty">No runs yet. Start one from the Run page.</div>
          ) : (
            <Table data-testid="runs-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Run</TableHead>
                  <TableHead>Range</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Orders</TableHead>
                  <TableHead className="text-right">PDFs</TableHead>
                  <TableHead>Download</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((r) => {
                  const downloadable = r.download_ready && (r.status === "completed" || r.status === "cookie_expired");
                  return (
                    <TableRow key={r.id} data-testid={`run-row-${r.id.slice(0,8)}`}>
                      <TableCell className="font-mono text-xs">{r.id.slice(0, 8)}…</TableCell>
                      <TableCell className="text-sm">{r.start_date} → {r.end_date}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(r.started_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant={VARIANT[r.status] || "secondary"} className="uppercase tracking-wider text-[10px]">
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">{r.total_orders_found}</TableCell>
                      <TableCell className="text-right text-sm">{r.pdfs_downloaded ?? 0}</TableCell>
                      <TableCell>
                        {downloadable ? (
                          <a
                            href={`${API}/runs/${r.id}/download`}
                            download={`amazon-invoices-${r.id}.zip`}
                            className="text-primary text-sm underline underline-offset-4"
                            data-testid={`history-download-${r.id.slice(0,8)}`}
                            title={`Download ${formatBytes(r.zip_size_bytes)}`}
                          >
                            ↓ {formatBytes(r.zip_size_bytes)}
                          </a>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Link to={`/runs/${r.id}`} className="text-primary text-sm underline underline-offset-4" data-testid={`view-run-link-${r.id.slice(0,8)}`}>
                          View
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
